import React from 'react';
import { Button, ButtonProps, Tooltip } from '@mui/material';
import { useWriteGuard } from '../../context/useWriteGuard';

/**
 * Write-action button — the per-affordance read-only gate.
 *
 * On a read-only env (viewing qa/uat, or a non-editable deployment) it renders
 * the button DISABLED with a tooltip explaining why; otherwise it's a plain MUI
 * <Button> with every prop forwarded. Drop-in replacement for <Button> at any
 * site that triggers a true MUTATION:
 *   - Create / Add / New
 *   - Delete / Remove
 *   - Save / Update / Publish / Apply
 *   - an in-place edit toggle (turns a row/section editable)
 *
 * Do NOT use it for read-safe controls — navigation (incl. "Edit" buttons that
 * only OPEN a record's detail page), View, Cancel, Close, Back, Refresh,
 * Export, Filter, Search, tabs. Those stay a plain <Button> so read-only
 * browsing keeps working.
 *
 * The server write-fence + axios non-GET reject remain the hard guarantee that
 * nothing persists; this is the affordance layer that makes read-only look and
 * feel read-only.
 *
 * Pass `ignoreEnvReadOnly` for platform-DATA mutations (Consumer 360 / Member
 * Import) that are legitimately allowed in any env.
 */
type WriteButtonProps = ButtonProps & { ignoreEnvReadOnly?: boolean };

const WriteButton = React.forwardRef<HTMLButtonElement, WriteButtonProps>(
  function WriteButton({ disabled, fullWidth, ignoreEnvReadOnly, ...rest }, ref) {
    const { readOnly: guardReadOnly, disabledProps } = useWriteGuard();
    const readOnly = ignoreEnvReadOnly ? false : guardReadOnly;
    const button = (
      <Button
        ref={ref}
        fullWidth={fullWidth}
        // When opted out (ignoreEnvReadOnly), this is a legitimate any-env data
        // op — do NOT mark it data-write-action, so ReadOnlyGuard won't block its
        // click (matters for buttons inside portaled modals, which fall outside
        // the page's data-readonly-interactive subtree).
        data-write-action={ignoreEnvReadOnly ? undefined : 'true'}
        {...rest}
        disabled={disabled || readOnly}
      />
    );
    if (!readOnly) return button;
    return (
      // A disabled button fires no events, so MUI needs a non-disabled wrapper
      // for the tooltip to receive hover. inline-flex keeps button sizing intact;
      // width:100% preserves a fullWidth button's layout.
      <Tooltip title={disabledProps.title ?? 'Read-only environment'} arrow>
        <span style={{ display: 'inline-flex', width: fullWidth ? '100%' : undefined }}>
          {button}
        </span>
      </Tooltip>
    );
  },
);

export default WriteButton;
